import React, { useState, useEffect, useCallback } from 'react';
import { DayPilotCalendar } from "@daypilot/daypilot-lite-react";
import { useOpointAuth } from '@opoint/authjs-react';
import { CircularProgress } from '@mui/material';
import { deleteAssignment, createAssignment, updateAssignment } from '../api';
import { Assignment, ConfigurationEmployee, ConfigurationTeam, Task } from '../types';
import AssignmentEditModal from './AssignmentEditModal';
import dayjs, { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("Asia/Calcutta");

interface Column {
    name: string;
    id: string;
    html?: string;
}

interface CalendarEvent {
    id: number;
    html: string;
    start: string;
    end: string;
    barColor: string;
    resource: string;
}

// From Chat-GPT
function getTextColor(bgColor: string): string {
    // Convert hex color to RGB
    const hexToRgb = (hex: string) => {
        let r: number, g: number, b: number;
        if (hex.length === 4) {
            r = parseInt(hex[1] + hex[1], 16);
            g = parseInt(hex[2] + hex[2], 16);
            b = parseInt(hex[3] + hex[3], 16);
        } else if (hex.length === 7) {
            r = parseInt(hex[1] + hex[2], 16);
            g = parseInt(hex[3] + hex[4], 16);
            b = parseInt(hex[5] + hex[6], 16);
        } else {
            throw new Error('Invalid hex color format');
        }
        return { r, g, b };
    };

    // Calculate luminance
    const calculateLuminance = ({ r, g, b }: { r: number, g: number, b: number }) => {
        const a = [r, g, b].map(v => {
            v /= 255;
            return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
        });
        return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
    };

    const rgb = hexToRgb(bgColor);
    const luminance = calculateLuminance(rgb);

    // Return black or white based on luminance
    return luminance > 0.25 ? '#000000' : '#FFFFFF';
}

const columnFromConfigurationEmployee = (
    member: ConfigurationEmployee,
): Column => {
    return {
        name: member.name,
        id: member.id_user.toString(),
        html: (
            "<div style='padding: 5px; color: " + getTextColor(member.color) + "; background-color: " + member.color + ";'>" + (
                (
                    member.is_absent
                    ? "<s>" + member.name + "</s>"
                    : (
                        member.is_leader
                        ? "<strong><em>" + member.name + "</em></strong>"
                        : member.name
                    )
                )
            ) + "</div>"
        ),
    };
}

const columnsFromConfigurationTeam = (team: ConfigurationTeam): Column[] => {
    return [
        ...team.members.map(
            (member) => columnFromConfigurationEmployee(member)
        ),
    ];
}

const editableHandlers = {
    timeRangeSelectedHandling: "Enabled",
    eventMoveHandling: "Update",
    eventResizeHandling: "Update",
    eventDeleteHandling: "Update",
    eventClickHandling: "Update",
    eventRightClickHandling: "Update",
};

const nonEditableHandlers = {
    timeRangeSelectedHandling: "Disabled",
    eventMoveHandling: "Disabled",
    eventResizeHandling: "Update",
    eventDeleteHandling: "Disabled",
    eventClickHandling: "Update",
    eventRightClickHandling: "Disabled",
};

const indiaBusinessBeginsHour = 8;
const getNorwayIndiaDifference = (date: Dayjs) => {
    const norway = dayjs.tz(date, "Europe/Oslo");
    const india = dayjs.tz(date, "Asia/Calcutta");
    return norway.utcOffset()/60 - india.utcOffset()/60;
}

interface CalendarProps {
    modulo: number;
    team: ConfigurationTeam;
    editable: boolean;
    selectedTask: Task | undefined;
    tasks: Task[];
    assignments: Assignment[];
    setAssignments: (assignments: Assignment[]) => void;
    date: Dayjs;
    useIndianTime: boolean;
}

const Calendar = (props: CalendarProps) => {
    const {
        modulo,
        team,
        editable,
        selectedTask,
        tasks,
        assignments,
        setAssignments,
        date,
        useIndianTime,
    } = props;

    const auth = useOpointAuth();

    const [events, setEvents] = useState<CalendarEvent[]>([]);
    const [assignmentEditModalOpen, setAssignmentEditModalOpen] = useState(false);
    const [clickedAssignment, setClickedAssignment] = useState(assignments[0]);

    // lots of timezone dependent conversions between calendar events and assignments
    const timezone = useIndianTime ? "Asia/Calcutta" : "Europe/Oslo";
    dayjs.tz.setDefault(timezone);
    const businessBeginsHour = (
        useIndianTime
        ? indiaBusinessBeginsHour
        : indiaBusinessBeginsHour+Math.round(getNorwayIndiaDifference(date))
    );

    const unixTimestampFromCalendarTime = useCallback(
        (calendarTime: string): number => {
            // TODO: figure out why the hour of the calendar times are
            // in relation to the start of the calendar, not the actual time
            return dayjs.tz(calendarTime).add(businessBeginsHour, 'hours').unix();
        }, [businessBeginsHour]);


    const calendarTimeFromUnixTimestamp = useCallback(
        (unixTimestamp: number): string => {
        const newTime = dayjs.tz(
            dayjs.unix(unixTimestamp),
        ).subtract(businessBeginsHour, 'hours').format('YYYY-MM-DDTHH:mm:ss');
        return newTime;
    }, [businessBeginsHour]);

    const eventFromAssignment = useCallback((
        tasks: Task[],
        assignment: Assignment,
    ): CalendarEvent => {
        const task = tasks.find(
            (task) =>
                task.id_task === assignment.id_task
        );
        let hasInstruction = assignment.instruction !== "";
        let hasComment = assignment.comment !== "";
        return {
            id: assignment.id_assignment!,
            html: (
                '<div>'
                + '<strong>' + (
                    task === undefined
                    ? "Unknown task"
                    : task.name
                )  + '</strong>'
                + '<em>'
                + '<br/>' + (
                    hasInstruction
                    ? assignment.instruction
                    : ""
                ) + '</em>'
                + (
                    hasComment
                    ? '<br/>' + assignment.comment
                    : ""
                )
                + '</div>'
            ),
            start: calendarTimeFromUnixTimestamp(assignment.start_time),
            end: calendarTimeFromUnixTimestamp(assignment.end_time),
            barColor: task !== undefined ? task.color : "gray",
            resource: assignment.id_user.toString(),
        };
    }, [calendarTimeFromUnixTimestamp]);

    useEffect(() => {
        setEvents(
            assignments
                .map(
                    (assignment) => eventFromAssignment(
                        tasks,
                        assignment,
                    )
                )
        );
    }, [assignments, tasks, eventFromAssignment]);

    const onTimeRangeSelected = useCallback(
        async (args: any) => {
            // The calendar outputs a timestamp relative to the businessBeginsHour for some reason,
            // so we have to add this here
            if (!editable) return;
            if (selectedTask === undefined) {
                console.log("No task selected!")
                return;
            };

            let start_time = unixTimestampFromCalendarTime(args.start.value);
            let end_time = unixTimestampFromCalendarTime(args.end.value);

            const newAssignment: Assignment = {
                id_task: selectedTask.id_task!,
                id_user: parseInt(args.resource, 10),
                modulo: modulo,
                start_time: start_time,
                end_time: end_time,
                instruction: "",
                comment: "",
            }
            const id_assignment = await createAssignment(auth, newAssignment);

            if (id_assignment) {
                let createdAssignment = {
                    id_assignment: id_assignment,
                    ...newAssignment,
                };
                setAssignments([
                    ...assignments,
                    createdAssignment,
                ]);
            }
        }, [
            auth,
            assignments,
            setAssignments,
            selectedTask,
            unixTimestampFromCalendarTime,
            editable,
            modulo
        ]);

    const onEventClick = async (args: any) => {
        const id_assignment = args.e.id();
        const clickedAssignment = assignments.find((assignment) => assignment.id_assignment === id_assignment);
        if (clickedAssignment === undefined) {
            console.log("Assignment not found");
            return;
        }
        console.log(clickedAssignment);
        setClickedAssignment(clickedAssignment);
        setAssignmentEditModalOpen(true);
    }

    const onEventDelete = async (args: any) => {
        let id_assignment = args.e.id();
        deleteAssignment(auth, id_assignment);
        setAssignments(assignments.filter(
            (assignment) => assignment.id_assignment !== id_assignment)
        );
    }

    const onEventResized = useCallback(
        async (args: any) => {
            let id_assignment = args.e.id();
            // For some reason the newEnd value here is not
            // incorrect in relation to the calendar like all
            // the other calendar event times are, so we can't
            // convert it using the function
            let endTimeUnixTimestamp = (
                dayjs.tz(args.newEnd.value)
                    .unix()
            );
            let updatedFields = {
                start_time: unixTimestampFromCalendarTime(args.newStart.value),
                end_time: endTimeUnixTimestamp,
            }
            console.log(updatedFields);
            updateAssignment(
                auth,
                id_assignment,
                updatedFields,
            );
            setAssignments(
                assignments.map(
                    (assignment) =>
                        assignment.id_assignment === id_assignment
                        ? {
                            ...assignment,
                            ...updatedFields,
                        }
                        : assignment
                )
            );
        }, [auth, assignments, setAssignments, unixTimestampFromCalendarTime]);

    const {
        eventClickHandling,
        eventDeleteHandling,
        eventMoveHandling,
        eventResizeHandling,
        timeRangeSelectedHandling,
    } = editable ? editableHandlers : nonEditableHandlers;

    if (team === undefined) {
        return <CircularProgress />;
    }

    const columns = columnsFromConfigurationTeam(team);

    return (
        <div className="calendar">
            <div>
                <AssignmentEditModal
                    isOpen={assignmentEditModalOpen}
                    setIsOpen={setAssignmentEditModalOpen}
                    assignment={clickedAssignment}
                    assignments={assignments}
                    setAssignments={setAssignments}
                />
            </div>
            <DayPilotCalendar
                viewType={"Resources"}
                theme={"lightblue"}
                eventClickHandling={eventClickHandling}
                eventDeleteHandling={eventDeleteHandling}
                eventMoveHandling={eventMoveHandling}
                eventResizeHandling={eventResizeHandling}
                timeRangeSelectedHandling={timeRangeSelectedHandling}
                businessBeginsHour={businessBeginsHour}
                businessEndsHour={businessBeginsHour+11}
                heightSpec={"BusinessHoursNoScroll"}
                allowEventOverlap={false}
                startDate={dayjs.tz(date).startOf('day').format('YYYY-MM-DDTHH:mm:ss')}
                columns={columns}
                events={events}
                onTimeRangeSelected={onTimeRangeSelected}
                onEventClick={onEventClick}
                onEventDelete={onEventDelete}
                onEventResized={onEventResized}
            />
        </div>
    );
}

export default Calendar;